import axios from "axios";

const CACHE_PREFIX = "inv_cache_v1:";
const OUTBOX_KEY = "inv_outbox_v1";
const DEFAULT_TTL_MS = 1000 * 60 * 60 * 12; 
const MAX_OUTBOX = 200;

function now() {
  return Date.now();
}

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

function stableStringify(obj) {
  if (!obj || typeof obj !== "object") return String(obj ?? "");
  const keys = Object.keys(obj).sort();
  const out = {};
  for (const k of keys) out[k] = obj[k];
  return JSON.stringify(out);
}

function getCacheKey(url, params = {}, extraKey = "") {
  const p = params ? stableStringify(params) : "";
  return `${CACHE_PREFIX}${url}::${p}::${extraKey || ""}`;
}

function setCache(key, data) {
  const payload = { ts: now(), data };
  localStorage.setItem(key, JSON.stringify(payload));
}

function getCache(key, ttlMs, allowExpired = false) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  const parsed = safeJsonParse(raw, null);
  if (!parsed || typeof parsed.ts !== "number") return null;

  const age = now() - parsed.ts;
  if (allowExpired) return parsed.data;
  if (age <= (ttlMs || DEFAULT_TTL_MS)) return parsed.data;
  return null;
}

export async function cachedFetchJson(
  url,
  { params = {}, headers = {}, ttlMs = DEFAULT_TTL_MS, cacheKeyExtra = "", timeoutMs = 25000 } = {}
) {
  const key = getCacheKey(url, params, cacheKeyExtra);
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;

  if (!offline) {
    try {
      const res = await axios.get(url, { params, headers, timeout: timeoutMs });
      setCache(key, res.data);
      return res.data;
    } catch (err) {
      const cached = getCache(key, ttlMs, false);
      if (cached != null) return cached;
      throw err;
    }
  }

  const cached = getCache(key, ttlMs, true);
  if (cached != null) return cached;

  const e = new Error("Sin conexión y sin cache disponible para esta consulta.");
  e.code = "OFFLINE_NO_CACHE";
  throw e;
}

function readOutbox() {
  const raw = localStorage.getItem(OUTBOX_KEY);
  const arr = safeJsonParse(raw, []);
  return Array.isArray(arr) ? arr : [];
}

function writeOutbox(arr) {
  localStorage.setItem(OUTBOX_KEY, JSON.stringify(Array.isArray(arr) ? arr : []));
}

export function outboxCount() {
  return readOutbox().length;
}

export function enqueueCount(payload) {
  const out = readOutbox();
  const item = {
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    ts: now(),
    type: "inventory_count",
    payload
  };
  out.push(item);
  if (out.length > MAX_OUTBOX) out.splice(0, out.length - MAX_OUTBOX);
  writeOutbox(out);
  return item;
}

export async function flushCounts(postFn, { max = 50 } = {}) {
  const out = readOutbox();
  if (!out.length) return { ok: true, sent: 0, left: 0 };

  let sent = 0;
  const remaining = [...out];

  while (remaining.length && sent < max) {
    const item = remaining[0];
    if (item.type !== "inventory_count") {
      remaining.shift();
      sent += 1;
      continue;
    }

    try {
      await postFn(item.payload);
      remaining.shift();
      sent += 1;
      writeOutbox(remaining);
    } catch (e) {
      writeOutbox(remaining);
      return { ok: false, sent, left: remaining.length, error: e?.message || String(e) };
    }
  }

  writeOutbox(remaining);
  return { ok: true, sent, left: remaining.length };
}
